import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextInput from '../text-input/TextInput';
import ButtonInput from '../button-input/ButtonInput';
import classNames from 'classnames';
import InputError from '../../../errors/InputError';
import compareObjectKeys from '../../../functions/compareObjectKeys';
import * as bp from '../../../../constants/breakpoints';
import './FilterInput.css';

export default class FilterInput extends Component {
  constructor(props) {
    super(props);

    this.mobileBreakpoint = bp.BREAKPOINT_MD;

    this.state = {
      allOptions: [],
      inputValue: "",
      isMobile: (window.innerWidth >= bp.BREAKPOINT_MD) ? false : true,
      selectedOptions: [],
      optionDisplay: false
    };
  }

  // Takes the options provided, translates into a standard object format, and sets as the state variable passed
  setOptions = (newOptions, stateVariable) => {
    let options = newOptions.filter((option, filterIndex) => {
      if (option &&
        (typeof option !== "object" ||
          !(option.id === undefined &&
            option.text === undefined
          )
        )) {
        let foundIndex = (typeof option === "object") ?
          newOptions.findIndex(otherOption => otherOption.id === option.id) :
          newOptions.findIndex(otherOption => otherOption === option);

        return (foundIndex === filterIndex);
      }

      return false;
    })
      .map((option) => {
        const id = (typeof option === "object") ?
          option.id :
          option;

        const text = (typeof option === "object") ?
          option.text :
          option;

        const selected = (stateVariable !== "selectedOptions") ? false : undefined;

        return { id, text, selected };
      });

    let newState = {};
    newState[stateVariable] = options;

    if (stateVariable === "selectedOptions") {
      this.selectOptions(options);
    }

    this.setState(newState);

    return options;
  }

  // Sees if the option type passed into the FilterInput was a string, and makes the option a string if needed
  formatOption = (option) => {
    return (this.props.options && typeof this.props.options[0] === "string") ?
      option.id :
      option;
  }

  // Shows and hides the filter option dropdown box
  toggleOptionDisplay = (calledState) => {
    this.setState((prevState) => {
      let optionDisplay;

      // ensures that when the options are open clicks within the component don't close them
      if (calledState === "click" && prevState.optionDisplay) {
        optionDisplay = prevState.optionDisplay;
      }
      else if (calledState === "blur") {
        optionDisplay = false;
      }
      else if (calledState === "focus") {
        optionDisplay = true;
      }
      else {
        optionDisplay = !prevState.optionDisplay;
      }

      return { optionDisplay }
    });
  }

  // Creates the JSX for the option list to display when dropped down
  buildOptionList = () => {
    let optionListItems;

    optionListItems = this.state.allOptions
      .filter((option) => (!option.selected && option.text.toLowerCase().indexOf(this.state.inputValue.toLowerCase()) !== -1));

    if (!optionListItems.length) {
      optionListItems = <li className="unselectable-option">No results found.</li>;
    }
    else {
      if (this.props.alphabetize) {
        optionListItems = optionListItems.sort(compareObjectKeys("text", true));
      }

      optionListItems = optionListItems
        .map((option) =>
          <li
            key={this.props.id + " " + option.id}
            className="selectable-option"
            onClick={() => this.selectOptions([option])}>
            {option.text}
          </li>
        );
    }

    let optionListDisplayClass = classNames('option-list', this.props.className, {
      'hide-options': !this.state.optionDisplay,
      'show-options': this.state.optionDisplay
    });

    return (
      <ul className={optionListDisplayClass} tabIndex="4">
        {optionListItems}
      </ul>
    );
  }

  // Creates list of closeable filters to display under the input
  buildSelectedResults = () => {
    return (this.state.selectedOptions.length !== 0) ?
      <ul className="closeable-filters">
        {
          this.state.selectedOptions.map((option, index) => {
            return <li key={option.id + index} className="closeable-filter">
              {option.text}
              <span className="closeable-filter__close-button" onClick={() => this.unselectOptions([option])}>
                x
              </span>
            </li>;
          })
        }
      </ul> :
      null;
  }

  // Figures out what kind of input is needed based on window width and returns the JSX for it
  buildInput = () => {
    if (this.props.isEditable && (!this.props.selectLimit || this.state.selectedOptions.length < this.props.selectLimit)) {
      if (!this.state.isMobile) {
        return (<TextInput
          autoComplete={false}
          className={this.props.className}
          id={this.props.id + "-text-input"}
          onChange={(event) => this.narrowFilters(event)}
          placeholderText={this.props.placeholderText}
        />);
      }
      else {
        let buttonClasses = classNames('filter-input__drop-button', {
          'filter-input__drop-button--arrow-up': this.state.optionDisplay
        });

        return (<ButtonInput
          className={buttonClasses}
          onClick={() => this.toggleOptionDisplay("click")}
        />);
      }
    }
    else {
      return null;
    }
  }

  // Updates filter option display based on text entered
  narrowFilters = (event) => {
    this.setState({ inputValue: event.target.value });
  }

  // Changes the boolean value of isMobile if the new state is different than the old one
  updateWidth = () => {
    let newIsMobile = (window.innerWidth >= bp.BREAKPOINT_MD) ? false : true;

    if (this.state.isMobile !== newIsMobile) {
      this.setState({ isMobile: newIsMobile });
    }
  }

  // Updates state to reflect newly chosen filters and calls function passed in for filterValueChange
  selectOptions = (selectedOptions) => {
    if (selectedOptions && selectedOptions.length) {
      this.setState((prevState) => {
        let noDupesSelectedOptions = selectedOptions
          .filter((option) => !prevState.selectedOptions
            .find((prevOption) => prevOption.id === option.id)
          );

        let newSelectedOptions = [...prevState.selectedOptions, ...noDupesSelectedOptions];

        let allOptions = prevState.allOptions;

        let optionObjects = newSelectedOptions.map((selectedOption) => allOptions.find((option) => option.id === selectedOption.id));

        optionObjects.forEach((optionObject) => {
          if (optionObject) {
            optionObject.selected = true;
          }
        });

        if (this.props.filterValueChange) {
          this.props.filterValueChange(newSelectedOptions.map((option) => this.formatOption(option)));
        }

        return { selectedOptions: newSelectedOptions, allOptions, optionDisplay: false };
      });
    }
  }

  // Updates state to reflect newly unchosen filters and calls function passed in for filterValueChange
  unselectOptions = (unselectedOptions) => {
    if (unselectedOptions && unselectedOptions.length) {
      this.setState((prevState) => {
        let allOptions = prevState.allOptions;
        let selectedOptions = prevState.selectedOptions;

        unselectedOptions.forEach((unselectedOption) => {
          selectedOptions = selectedOptions.filter((option) => option.id !== unselectedOption.id);

          let optionObject = allOptions.find((option) => option.id === unselectedOption.id);

          if (optionObject) {
            optionObject.selected = false;
          }
        });

        if (this.props.filterValueChange) {
          this.props.filterValueChange(selectedOptions.map((option) => this.formatOption(option)));
        }

        return { selectedOptions, allOptions };
      });
    }
  }

  // Gets the values of the filter input, and potentially returns an error if applicable
  getValue = () => {
    // if options were passed in as an array of strings, give an array of strings back - it's only polite.
    let selectedOptions = (this.props.options && typeof this.props.options[0] === "string") ?
      this.state.selectedOptions.map((option) => option.id) :
      this.state.selectedOptions;

    if (this.props.required && !selectedOptions.length) {
      throw new InputError(this.props.label + " is Required", selectedOptions);
    }

    return selectedOptions;
  }

  componentDidMount() {
    this.setOptions(this.props.options, "allOptions");
    this.setOptions(this.props.existingContentArray, "selectedOptions");
    window.addEventListener('resize', this.updateWidth);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWidth);
  }

  componentWillReceiveProps(newProps) {
    if (newProps.options !== this.props.options) {
      let newAllOptions = this.setOptions(newProps.options, "allOptions");

      // Check if there are options that need to be unselected because they're no longer in the options list, and unselect them.
      if (this.state.selectedOptions) {
        let unselectedOptions = [];
        this.state.selectedOptions.forEach((selectedOption) => {
          let existsInArray = newAllOptions.find((option) => option.id === selectedOption.id);

          if (!existsInArray) {
            unselectedOptions.push(selectedOption);
          }
        })

        if (unselectedOptions.length) {
          this.unselectOptions(unselectedOptions);
        }
      }
    }
  }

  render() {
    let verticalSpacingClass = this.props.verticalSpacing === "compact" ? "field--compact" : "field";
    let mobileClass = (this.state.isMobile) ? "filter-input__mobile" : "";

    const label = (this.props.label) ?
      <label htmlFor={this.props.id + "-text-input"}>
        {this.props.label}
      </label> :
      null;

    const requiredFieldIndicator = (this.props.required) ?
      <abbr title="This field is required"></abbr> :
      null;

    let errorMessage = (this.props.errorMessage) ?
      <span className="error-message">
        {this.props.errorMessage}
      </span> :
      null;

    let input = this.buildInput();
    let optionList = this.buildOptionList();
    let selectedResults = (this.props.showPills) ? this.buildSelectedResults() : null;

    let inputDisplay = (this.props.isEditable) ?
      <span id={this.props.id}>
        {label}
        {requiredFieldIndicator}
        <div
          className="filter-input__input-wrapper"
          onFocus={() => this.toggleOptionDisplay("focus")}
          onBlur={() => this.toggleOptionDisplay("blur")}
        >
          {input}
          {optionList}
          {selectedResults}
        </div>
        {errorMessage}
      </span> :
      <span>
        {label}
        <span className="read-only">
          {this.state.selectedOptions.map((option) => option.text).join(", ")}
        </span>
      </span>;

    return (
      <div className={`filter-input ${verticalSpacingClass} ${mobileClass}`}>
        {inputDisplay}
      </div>
    );
  }
}

FilterInput.defaultProps = {
  alphabetize: false,
  existingContentArray: [],
  isEditable: true,
  showPills: true
}

FilterInput.propTypes = {
  alphabetize: PropTypes.bool,
  className: PropTypes.string,
  errorMessage: PropTypes.string,
  existingContentArray: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]),
  filterValueChange: PropTypes.func,
  id: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]).isRequired,
  placeholderText: PropTypes.string,
  required: PropTypes.bool,
  selectLimit: PropTypes.number,
  showPills: PropTypes.bool,
  verticalSpacing: PropTypes.string
}